import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, MessageCircle, ShieldCheck, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { formatINR } from "@/lib/utils-format";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import { trackBeginCheckout, trackPurchase } from "@/lib/analytics";

export default function Checkout() {
    const { cart, subtotal, count, clear } = useCart();
    const [form, setForm] = useState({
        customer_name: "",
        customer_mobile: "",
        customer_email: "",
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        pincode: "",
        payment_method: "razorpay",
        delivery_method: "delivery",
        notes: "",
    });
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState({ whatsapp_number: "919971925446", razorpay_enabled: true, razorpay_configured: false });
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Checkout | signiastylettoix.com";
        api.get("/settings/public").then((r) => {
            setSettings(r.data);
            // Fallback: if Razorpay is not actually configured on this server,
            // default to WhatsApp checkout so the user never sees a broken button.
            if (r.data.razorpay_configured === false || r.data.razorpay_enabled === false) {
                setForm((f) => ({ ...f, payment_method: "whatsapp" }));
            }
        }).catch(() => {});
        try {
            trackBeginCheckout({ items: cart.items, value: subtotal, coupon: "" });
        } catch (e) { /* analytics never breaks app */ }
    }, []);

    if (count === 0) {
        return (
            <section className="section">
                <div className="container-x max-w-[600px] text-center">
                    <h1 className="font-serif text-[40px] md:text-[52px]">Cart is empty.</h1>
                    <Link to="/products" className="btn-primary mt-6 inline-flex">Shop now <ArrowRight size={16} /></Link>
                </div>
            </section>
        );
    }

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const total = subtotal;

    const loadRazorpayScript = () =>
        new Promise((resolve) => {
            if (window.Razorpay) return resolve(true);
            const s = document.createElement("script");
            s.src = "https://checkout.razorpay.com/v1/checkout.js";
            s.onload = () => resolve(true);
            s.onerror = () => resolve(false);
            document.body.appendChild(s);
        });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.customer_name || !form.customer_mobile) {
            toast.error("Name and mobile are required");
            return;
        }
        setLoading(true);
        try {
            const items = cart.items.map((i) => ({
                product_slug: i.product_slug,
                product_name: i.product_name,
                qty: i.qty,
                side: i.side,
                color: i.color,
                price: i.price,
            }));
            const payload = { ...form, items, coupon_code: "" };
            const { data: order } = await api.post("/orders", payload);

            // ── Razorpay online payment path ─────────────────────────────
            if (form.payment_method === "razorpay") {
                const ok = await loadRazorpayScript();
                if (!ok) {
                    toast.error("Could not load Razorpay. Please try WhatsApp checkout.");
                    setLoading(false);
                    return;
                }
                let rzpMeta;
                try {
                    const { data } = await api.post("/payments/razorpay/create", { order_id: order.id });
                    rzpMeta = data;
                } catch (err) {
                    toast.error(err.response?.data?.detail || "Razorpay unavailable — using WhatsApp checkout.");
                    // fallback: still succeed as WhatsApp order
                    try {
                        trackPurchase({ orderNumber: order.order_number, items: cart.items, value: order.total, coupon: "", paymentMethod: "whatsapp" });
                    } catch (e) { /* analytics never breaks app */ }
                    clear();
                    navigate(`/order-success/${order.order_number}`);
                    return;
                }
                const rzp = new window.Razorpay({
                    key: rzpMeta.key_id,
                    amount: rzpMeta.amount,
                    currency: rzpMeta.currency,
                    order_id: rzpMeta.razorpay_order_id,
                    name: "signiastylettoix.com",
                    description: `Order ${order.order_number}`,
                    prefill: {
                        name: form.customer_name,
                        contact: form.customer_mobile,
                        email: form.customer_email,
                    },
                    theme: { color: "#003E4D" },
                    handler: async (resp) => {
                        try {
                            await api.post("/payments/razorpay/verify", {
                                order_id: order.id,
                                razorpay_order_id: resp.razorpay_order_id,
                                razorpay_payment_id: resp.razorpay_payment_id,
                                razorpay_signature: resp.razorpay_signature,
                            });
                            try { trackPurchase({ orderNumber: order.order_number, items: cart.items, value: order.total, coupon: "", paymentMethod: "razorpay" }); } catch (e) { /* never break */ }
                            toast.success(`Payment successful — Order ${order.order_number}`);
                            clear();
                            navigate(`/order-success/${order.order_number}`);
                        } catch {
                            toast.error("Payment could not be verified. Our team will call you.");
                        }
                    },
                    modal: {
                        ondismiss: () => {
                            toast.info("Payment cancelled — order kept as pending. WhatsApp us to complete.");
                            setLoading(false);
                        },
                    },
                });
                rzp.open();
                return;
            }

            // ── Non-Razorpay flow (WhatsApp / COD / Clinic) ──────────────
            try {
                trackPurchase({ orderNumber: order.order_number, items: cart.items, value: order.total, coupon: "", paymentMethod: order.payment_method });
            } catch (e) { /* analytics never breaks app */ }
            toast.success(`Order ${order.order_number} placed!`);
            clear();
            navigate(`/order-success/${order.order_number}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Order failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="section">
            <div className="container-x grid lg:grid-cols-12 gap-10">
                <form onSubmit={submit} className="lg:col-span-7" data-testid="checkout-form">
                    <h1 className="font-serif text-[40px] md:text-[52px] leading-tight">Checkout</h1>
                    <p className="eyebrow mt-3">Step 1 — Your details</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        <Inp data-testid="co-name" required placeholder="Full name *" value={form.customer_name} onChange={set("customer_name")} />
                        <Inp data-testid="co-mobile" required placeholder="Mobile *" value={form.customer_mobile} onChange={set("customer_mobile")} />
                        <Inp data-testid="co-email" type="email" placeholder="Email" value={form.customer_email} onChange={set("customer_email")} className="md:col-span-2" />
                    </div>

                    <p className="eyebrow mt-10">Delivery</p>
                    <div className="flex gap-2 mt-3">
                        {["delivery", "clinic_pickup"].map((m) => (
                            <button
                                key={m}
                                type="button"
                                onClick={() => setForm({ ...form, delivery_method: m })}
                                className={`px-4 py-2 rounded-full text-[13px] border transition ${form.delivery_method === m ? "border-[#003E4D] text-[#003E4D] bg-[#003E4D]/[0.05]" : "border-[#E5E7EB] text-[#4a5568]"}`}
                                data-testid={`co-delivery-${m}`}
                            >
                                {m === "delivery" ? "Home delivery" : "Clinic pickup"}
                            </button>
                        ))}
                    </div>
                    {form.delivery_method === "delivery" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <Inp data-testid="co-addr1" placeholder="Address line 1" value={form.address_line1} onChange={set("address_line1")} className="md:col-span-2" />
                            <Inp data-testid="co-addr2" placeholder="Address line 2" value={form.address_line2} onChange={set("address_line2")} className="md:col-span-2" />
                            <Inp data-testid="co-city" placeholder="City" value={form.city} onChange={set("city")} />
                            <Inp data-testid="co-state" placeholder="State" value={form.state} onChange={set("state")} />
                            <Inp data-testid="co-pincode" placeholder="Pincode" value={form.pincode} onChange={set("pincode")} className="md:col-span-2" />
                        </div>
                    )}

                    <p className="eyebrow mt-10">Payment</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                        {settings.razorpay_enabled !== false && settings.razorpay_configured !== false && (
                            <PayBtn active={form.payment_method === "razorpay"} onClick={() => setForm({ ...form, payment_method: "razorpay" })} testid="co-pay-razorpay">
                                <CreditCard size={14} /> Pay online (Cards · UPI · NetBanking)
                            </PayBtn>
                        )}
                        <PayBtn active={form.payment_method === "whatsapp"} onClick={() => setForm({ ...form, payment_method: "whatsapp" })} testid="co-pay-whatsapp">Pay via WhatsApp</PayBtn>
                        <PayBtn active={form.payment_method === "cod"} onClick={() => setForm({ ...form, payment_method: "cod" })} testid="co-pay-cod">Cash / UPI on delivery</PayBtn>
                        <PayBtn active={form.payment_method === "clinic"} onClick={() => setForm({ ...form, payment_method: "clinic" })} testid="co-pay-clinic">Pay at clinic</PayBtn>
                    </div>
                    {form.payment_method === "razorpay" && (
                        <p className="text-[12px] text-[#4a5568] mt-3 flex items-start gap-2">
                            <ShieldCheck size={14} className="text-[#003E4D] mt-0.5 shrink-0" />
                            Secure payment via Razorpay · All cards, UPI, NetBanking &amp; wallets · No-Cost EMI available on eligible cards.
                        </p>
                    )}

                    <div className="mt-10 p-4 rounded-2xl bg-[#f8f9fa] border border-black/5 text-[12.5px] text-[#4a5568] leading-relaxed flex gap-3">
                        <ShieldCheck size={18} className="text-[#003E4D] mt-0.5 shrink-0" />
                        <span>Hearing aid performance depends on a hearing test and fitting. A HearClear audiologist may contact you for proper configuration before dispatch.</span>
                    </div>

                    <button type="submit" disabled={loading} className="btn-primary mt-7 w-full justify-center md:w-auto !px-10" data-testid="co-submit">
                        {loading ? "Processing…" : (form.payment_method === "razorpay" ? "Pay & Place Order" : "Place Order")} <ArrowRight size={16} />
                    </button>
                </form>

                <aside className="lg:col-span-5">
                    <div className="bg-[#f8f9fa] rounded-3xl p-7 lg:sticky lg:top-24" data-testid="checkout-summary">
                        <h3 className="font-serif text-[24px]">Your order</h3>
                        <div className="mt-5 space-y-3 max-h-[260px] overflow-y-auto">
                            {cart.items.map((it, i) => (
                                <div key={i} className="flex gap-3 items-center">
                                    <img src={it.image} alt={it.product_name} className="w-14 h-14 object-contain bg-white rounded-lg" />
                                    <div className="flex-1">
                                        <p className="text-[13.5px] font-medium leading-tight">{it.product_name}</p>
                                        <p className="text-[11.5px] text-[#4a5568] mt-0.5">{it.qty} × {formatINR(it.price)}</p>
                                    </div>
                                    <span className="text-[13px] font-semibold">{formatINR(it.qty * it.price)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-5 pt-5 border-t border-[#E5E7EB] space-y-2 text-[13.5px]">
                            <div className="flex items-center justify-between"><span className="text-[#4a5568]">Subtotal</span><span>{formatINR(subtotal)}</span></div>
                            <div className="flex items-center justify-between"><span className="text-[#4a5568]">Delivery</span><span>Free</span></div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-[#E5E7EB] flex items-end justify-between">
                            <span className="text-[14px] text-[#4a5568]">Total</span>
                            <span className="font-serif text-[28px]" data-testid="summary-total">{formatINR(total)}</span>
                        </div>
                        <WhatsAppCTA
                            waNumber={settings.whatsapp_number}
                            message="Hi, I'm checking out on signiastylettoix.com."
                            sourcePage="checkout"
                            className="btn-whatsapp w-full justify-center mt-5"
                            data-testid="co-whatsapp"
                        >
                            <MessageCircle size={16} /> Need help? WhatsApp us
                        </WhatsAppCTA>
                    </div>
                </aside>
            </div>
        </section>
    );
}

const Inp = ({ className = "", ...props }) => (
    <input className={`h-12 px-4 rounded-xl border border-[#E5E7EB] bg-white text-[15px] focus:outline-none focus:border-[#003E4D] focus:ring-2 focus:ring-[#003E4D]/15 ${className}`} {...props} />
);
const PayBtn = ({ active, children, onClick, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] border transition ${active ? "border-[#003E4D] text-[#003E4D] bg-[#003E4D]/[0.05]" : "border-[#E5E7EB] text-[#4a5568] hover:border-[#003E4D]"}`}>{children}</button>
);
